import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
  alpha,
  Paper,
  AlertTitle
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Image as ImageIcon } from '@mui/icons-material';
import axiosInstance from '../axiosInstance';
import { useTheme } from '@mui/material/styles';

interface ProfileImageAccordionProps {
  expanded: string | false;
  handleAccordionChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  userData: any;
  setUserData: (userData: any) => void;
}

const ProfileImageAccordion: React.FC<ProfileImageAccordionProps> = ({
  expanded,
  handleAccordionChange,
  userData,
  setUserData
}) => {
  const [newProfileImagePath, setNewProfileImagePath] = useState('');
  const [profileImageError, setProfileImageError] = useState('');
  const [profileImageSuccess, setProfileImageSuccess] = useState('');
  const theme = useTheme();
  const isDarkTheme = theme.name === 'dark';

  const handleProfileImageChange = async () => {
    if (!userData) return;

    try {
      const response = await axiosInstance.put(
        `/auth/users/${userData.user_id}/profile-image`,
        new URLSearchParams({
          profile_image_path: newProfileImagePath
        })
      );

      if (response.status === 200) {
        setUserData({ ...userData, profile_image_path: newProfileImagePath });
        setProfileImageSuccess('Profile image updated successfully');
        setProfileImageError('');
      }
    } catch (error) {
      setProfileImageError('Error updating profile image. Please try again.');
      console.error('Error updating profile image:', error);
    }
  };

  return (
    <Accordion
      expanded={expanded === 'panel2'}
      onChange={handleAccordionChange('panel2')}
      sx={{ 
        width: '100%', 
        maxWidth: '100%',
        backgroundColor: isDarkTheme ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        borderLeft: expanded === 'panel2' ? '4px solid' : '1px solid',
        borderColor: expanded === 'panel2' ? 'primary.main' : 'divider',
        transition: 'all 0.3s ease'
      }}
    >
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&.Mui-expanded': {
            minHeight: '48px',
            bgcolor: isDarkTheme ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.05)
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ImageIcon sx={{ mr: 1.5, color: theme.palette.primary.main }} />
          <Typography variant="h6">Change Profile Image</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : alpha(theme.palette.background.secondary, 0.2),
            borderRadius: isDarkTheme ? '8px' : '0',
            mb: 2
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Enter the URL of your new profile image. The image will be displayed on your profile and in forum posts.
          </Typography>
        </Paper>

        <Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ width: '100%' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="New Profile Image URL"
                type="text"
                fullWidth
                margin="normal"
                value={newProfileImagePath}
                onChange={(e) => setNewProfileImagePath(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: isDarkTheme ? '8px' : '0',
                  }
                }}
              />
            </Grid>
            {profileImageError && (
              <Grid item xs={12}>
                <Alert 
                  severity="error" 
                  sx={{ 
                    borderRadius: isDarkTheme ? '8px' : '0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '& .MuiAlert-icon': {
                      color: theme.palette.error.main
                    }
                  }}
                >
                  <AlertTitle>Error</AlertTitle>
                  {profileImageError}
                </Alert>
              </Grid>
            )}
            {profileImageSuccess && (
              <Grid item xs={12}>
                <Alert 
                  severity="success" 
                  sx={{ 
                    borderRadius: isDarkTheme ? '8px' : '0',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '& .MuiAlert-icon': {
                      color: theme.palette.success.main
                    }
                  }}
                >
                  <AlertTitle>Success</AlertTitle>
                  {profileImageSuccess}
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleProfileImageChange}
                sx={{ 
                  mt: 2, 
                  borderRadius: isDarkTheme ? '4px' : '0px',
                  textTransform: 'none',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s ease'
                }}
                fullWidth
              >
                Update Profile Image
              </Button>
            </Grid>
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProfileImageAccordion;
